import React, { Component } from 'react';
import { Button, Table, Segment, Grid, Label, Tab, TabProps } from 'semantic-ui-react';
import { push } from 'connected-react-router';
import Info from '@/components/Info';
import * as actions from '@/store/actions';
import BillingForm from '@/components/BillingForm';
import { differenceInDays, isBefore } from '@/utils/datetime';
import { Dispatch } from 'redux';
import { TrialType, ListState, LicenseType, LicensesInformation, License, LicensePlanId, LicensePrice } from '@/types';
import { LocalizeContextProps } from 'react-localize-redux';
import classnames from 'classnames/bind';
import styles from './Licenses.module.css';
import CheckIconImg from '../../pngs/done@2x.png';
import invalidIconImg from '../../pngs/invalid-name@2x.png';
import { groupBy } from 'lodash';
import { ADD_OR_CONTINUE_TRIAL_CLICKED } from '@/utils/constants';
import { RouteComponentProps } from 'react-router-dom';
import {
    groupByPricePlan,
    hasVossPlan,
    findPlanPrice,
    planDisplayPrice,
    PlanLike,
    isApplicationLicenseTypeId,
    isLicenseDowngrade,
    planOf,
} from '@/utils/common';
import DowngradeLicenseModal from '@/components/DowngradeLicenseModal';
import qs from 'qs';
import { ApplicationState } from '@/store';



const cx = classnames.bind(styles);

const PLAN_LABEL_KEYS: Partial<Record<number, string>> = {
    [LicensePlanId.Monthly]: 'licenses.tabs.monthly',
    [LicensePlanId.Quarterly]: 'licenses.tabs.quarterly',
    [LicensePlanId.Yearly]: 'licenses.tabs.yearly',
};

interface Props extends LocalizeContextProps, RouteComponentProps {
    onGoBack?: () => void;
    onSelectPlan?: () => void;
    onContinueTrial?: () => void;
    onUpgradeLicence?: () => void;
    dispatch: Dispatch;
    types: ListState<LicenseType>;
    companyLicenses: ListState<License>;
    information: ListState<LicensesInformation>;
    billing: ApplicationState['billing'];
    licenseInformationTypes: ListState<any>;
    licensePlans: ListState<any>;
    modal: any;
    addTrial: any;
    suggestedLicense?: any;
    suggestedLicenseId?: number;
    suggestedTrialType?: TrialType;
    currentTrialPeriod?: any;
    autofill?: boolean;
}
interface State {
    step: number;
    licenseTypeId: number | null;
    licensePlanId: LicensePlanId | null,
    pendingDowngrade: { licenseTypeId: number; licensePlanId: number } | null;
}

export default class Licenses extends Component<Props, State> {
    static defaultProps = {
        onSelectPlan: () => { },
        onGoBack: () => { },
        currentTrialPeriod: {},
    };

    state: State = {
        step: 0,
        licenseTypeId: null,
        licensePlanId: null as LicensePlanId | null,
        pendingDowngrade: null,
    };

    componentDidMount() {
        const { activeTab } = qs.parse(this.props.location.search, { ignoreQueryPrefix: true });

        if (activeTab && typeof activeTab === 'string') {
            this.props.dispatch(push({ hash: activeTab }))
        }
    }

    handleBack = () => {
        // TODO Handle UX
        this.setState({ step: this.state.step - 1 });
        if (this.state.step === 1 && this.props.onGoBack) {
            this.props.onGoBack();
        }
    };

    choosePlan = (licenseTypeId: number, licensePlanId?: number) => {
        this.setState({ licenseTypeId, step: 1, licensePlanId: licensePlanId ? licensePlanId : null });

        if (this.props.onSelectPlan) {
            this.props.onSelectPlan();
        }
    };

    handleSelectPlan = (licenseTypeId: number, licensePlanId: number) => {
        const current = findCurrentApplicationLicense(this.props.companyLicenses.data);

        if (isLicenseDowngrade(current?.TypeId, licenseTypeId)) {
            this.setState({ pendingDowngrade: { licenseTypeId, licensePlanId } });
            return;
        }

        this.choosePlan(licenseTypeId, licensePlanId);
    };

    confirmDowngrade = () => {
        const { pendingDowngrade } = this.state;
        this.setState({ pendingDowngrade: null });
        if (pendingDowngrade) {
            this.choosePlan(pendingDowngrade.licenseTypeId, pendingDowngrade.licensePlanId);
        }
    };

    handleSubmitSuccess = () => {
        if (this.props.onUpgradeLicence) {
            this.props.onUpgradeLicence();
        }
        this.setState({ step: 0 });
        this.props.dispatch({
            ...actions.FETCH_COMPANY_LICENSES.request({ IncludeVossSubscription: true }),
            meta: { requestIsHidden: true },
        });
    };

    render() {
        const { suggestedTrialType } = this.props;

        const isExtraLicence = this.props.modal?.exceedingData?.SuggestedLicenseToUpgrade
            ?.IsExtraLicenseOption;

        return (
            <>
                {this.state.step === 0 &&
                    isExtraLicence &&
                    !suggestedTrialType &&
                    this.renderUpgradeNeeded()}
                {this.state.step === 0 &&
                    !suggestedTrialType &&
                    !isExtraLicence &&
                    this.renderSelectPlan()}
                {this.state.step === 0 && suggestedTrialType && this.renderTrialNeeded()}
                {this.state.pendingDowngrade && (
                    <DowngradeLicenseModal
                        periodEndDate={
                            findCurrentApplicationLicense(this.props.companyLicenses.data)
                                ?.VossSubscription?.SubscriptionBillingPeriodEndDate
                        }
                        onConfirm={this.confirmDowngrade}
                        onCancel={() => this.setState({ pendingDowngrade: null })}
                    />
                )}
                {this.state.step === 1 && (
                    <Segment basic>
                        <BillingForm
                            licenseTypeId={this.state.licenseTypeId}
                            licensePlanId={this.state.licensePlanId}
                            onBack={this.handleBack}
                            onSubmitSuccess={this.handleSubmitSuccess}
                            autofill={this.props.autofill}
                            action={actions.CREATE_COMPANY_LICENSE_WITH_UPDATE_BILLING_INFO}
                        />
                    </Segment>
                )}
            </>
        );
    }

    renderTrialNeeded = () => {
        const {
            suggestedTrialType,
            suggestedLicense,
            addTrial,
            currentTrialPeriod,
            translate,
        } = this.props;

        if (!suggestedLicense) {
            return (
                <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                    <Button
                        content={`${translate('licenses.testForFree')} ${suggestedTrialType.TrialDays
                            } ${translate('common.days')}`}
                        icon="star"
                        basic
                        primary
                        onClick={this.handleAddTrial}
                        loading={addTrial.isLoading}
                    />
                    <Button
                        onClick={() => {
                            this.props.dispatch(push('/account'));
                            this.props.dispatch({
                                type: 'HIDE_LICENSES_MODAL',
                            });
                        }}
                        content={`${translate('licenses.goToSubscriptions')}`}
                    />
                </div>
            );
        }

        const formattedPrice = this.getBillingPlanPriceSuffix(suggestedLicense);

        return (
            <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                {!currentTrialPeriod.Active &&
                    !isBefore(currentTrialPeriod.ValidTo, new Date()) && (
                        <Button
                            content={`${translate('licenses.testForFree')} ${suggestedTrialType.TrialDays
                                } ${translate('common.days')}`}
                            icon="star"
                            basic
                            primary
                            onClick={this.handleAddTrial}
                            loading={addTrial.isLoading}
                        />
                    )}
                {currentTrialPeriod.Active && (
                    <Button
                        content={`${translate('licenses.continueTrial')} (${differenceInDays(
                            currentTrialPeriod.ValidTo,
                            new Date()
                        )}) ${translate('licenses.daysLeft')}`}
                        icon="star"
                        basic
                        primary
                        onClick={() => {
                            if (this.props.onContinueTrial) {
                                this.props.onContinueTrial();
                            }
                            sessionStorage.setItem(ADD_OR_CONTINUE_TRIAL_CLICKED, 'true');
                        }}
                    />
                )}
                <Button
                    content={`${translate('licenses.upgradeAccount')}${formattedPrice}`}
                    icon="check"
                    onClick={() => this.choosePlan(suggestedLicense.Id)}
                    primary
                />
            </div>
        );
    };

    handleAddTrial = () => {
        this.props.dispatch(
            actions.ADD_TRIAL.request({ TrialTypeId: this.props.suggestedTrialType.Id })
        );
    };

    handleTabChange = (e: React.MouseEvent, { activeIndex, panes }: TabProps) => {
        const { history, location } = this.props;
        // console.log(this)
        history.push(
            // Check if activeIndex property is valid
            // @ts-ignore
            `${location.pathname}#${panes[activeIndex].menuItem.key}`
        );
    }

    renderSelectPlan = () => {
        const { translate, location, licensePlans, billing } = this.props;
        const licensePlanId = billing.settings?.data?.LicensePlanId;
        const { hash } = location;


        const panes = licensePlans.data.filter(hasVossPlan).map((plan: any) => {
            const labelKey = PLAN_LABEL_KEYS[plan.Id];

            return {
                menuItem: {
                    content: labelKey ? (translate(labelKey) as string) : plan.Name,
                    // plan.Name is localized by the API; the id keeps the URL hash and ?activeTab= ASCII.
                    key: String(plan.Id),
                    id: plan.Id,
                },
                render: () => <Tab.Pane>{this.renderPricePlan(plan.Id)}</Tab.Pane>,
            }
        })

        const tabKey = hash.slice(1);
        const hashIndex = tabKey ? panes.findIndex((pane: any) => pane.menuItem.key === tabKey) : -1;
        const billingIndex = licensePlanId
            ? panes.findIndex((pane: any) => pane.menuItem.id === licensePlanId)
            : -1;

        const activeIndex = hashIndex !== -1 ? hashIndex : billingIndex !== -1 ? billingIndex : 0;

        return (
            <Tab
                panes={panes}
                onTabChange={this.handleTabChange}
                activeIndex={activeIndex}
            />
        );
    };
    // TODO: render only when not loading, wait with rendering ?
    renderUpgradeNeeded = () => {
        const { translate, modal } = this.props;
        const { SuggestedLicenseToUpgrade } = modal.exceedingData;

        return (
            <>
                <Grid>
                    <Grid.Column textAlign="right">
                        <Button
                            type="button"
                            primary
                            icon="check"
                            content={`${translate('form.buy')} - ${this.getPriceString(
                                SuggestedLicenseToUpgrade
                            )}`}
                            floated="right"
                            onClick={() => this.choosePlan(SuggestedLicenseToUpgrade.Id)}
                        />
                    </Grid.Column>
                </Grid>
            </>
        );
    };

    // Price row and plan matching the company billing plan.
    getBillingPlanPrice = (type: LicenseType) => {
        const { licensePlans, billing } = this.props;
        const billingPlanId = billing.settings?.data?.LicensePlanId;
        const planPrice = findPlanPrice(type?.Prices, billingPlanId);
        const plan = licensePlans.data.find((licensePlan: any) => licensePlan.Id === billingPlanId);

        return planPrice?.Price != null ? { planPrice, amount: planPrice.Price, plan } : undefined;
    };

    getPriceString = (type: LicenseType) => {
        const { translate } = this.props;
        const billingPlanPrice = this.getBillingPlanPrice(type);

        if (!billingPlanPrice) {
            return '';
        }

        const { planPrice, amount, plan } = billingPlanPrice;
        const sign = planPrice.Country?.CurrencyInfo?.CurrencySign;
        const { value, perMonth } = planDisplayPrice(amount, plan);

        if (perMonth) {
            return `${value}${sign} / ${translate('common.month')}`.toLowerCase();
        } else if (plan) {
            return `${amount}${sign} ${plan.Name}`.toLowerCase();
        }

        return '';
    };

    // Price suffix for the trial-needed upgrade button: " - 289kr / month", " - 100kr" or nothing.
    getBillingPlanPriceSuffix = (type: LicenseType) => {
        const { translate } = this.props;
        const billingPlanPrice = this.getBillingPlanPrice(type);

        if (!billingPlanPrice) {
            return '';
        }

        const { planPrice, amount, plan } = billingPlanPrice;
        const sign = planPrice.Country?.CurrencyInfo?.CurrencySign;
        const { value, perMonth } = planDisplayPrice(amount, plan);

        return perMonth ? ` - ${value}${sign} / ${translate('common.month')}` : ` - ${value}${sign}`;
    };

    getPlanPrice = (planPrice: LicensePrice, plan: PlanLike | undefined) => {
        const { translate } = this.props;
        const { value, perMonth } = planDisplayPrice(planPrice.Price as number, plan);
        const sign = planPrice.Country?.CurrencyInfo?.CurrencySign;

        return { priceValue: value, unit: perMonth ? `${sign}/${translate('common.month')}` : sign };
    };

    renderPricePlan = (pricePlanId: number) => {
        const { translate, information, types, companyLicenses, suggestedLicenseId, licenseInformationTypes, billing, licensePlans } = this.props;
        const billingPlanId = billing.settings?.data?.LicensePlanId;

        let matchingLicenses = groupByPricePlan(types.data)[pricePlanId];

        const loading = information.isLoading || types.isLoading || companyLicenses.isLoading;
        // A hidden refetch keeps its data but still clears isLoaded; keep the table mounted meanwhile.
        const isLoaded = isReady(information) && isReady(types) && isReady(companyLicenses);
        const grouppedByInformationType = groupBy(information.data, 'LicenseInformationTypeId');

        return (
            <Segment className={cx('table-segment')} basic loading={loading}>
                {isLoaded && (
                    <>
                        <Table className={cx('plans-table')} basic="very" compact size="small" unstackable>
                            <Table.Header>
                                <Table.Row>
                                    <Table.HeaderCell className={cx('plan-cell')} textAlign="center">
                                        <div className={cx('plan-head', 'plan-head-label')}>
                                            <span className={cx('plan-name')}>{translate('license.ourFeatures')}</span>
                                        </div>
                                    </Table.HeaderCell>
                                    {types.data.map((type) => {
                                        const license = matchingLicenses?.find(plan => type.Name === plan.Name);
                                        const planPrice = license?.Prices.find(p => p.LicensePlanId === pricePlanId);
                                        const displayPrice = planPrice?.Price
                                            ? this.getPlanPrice(
                                                planPrice,
                                                licensePlans.data.find((plan: any) => plan.Id === pricePlanId)
                                            )
                                            : undefined;
                                        const isSuggested = type.Id === suggestedLicenseId;
                                        const isActive = !!type.Name && !!findLicense(companyLicenses.data, type.Id, pricePlanId, billingPlanId, isLive);
                                        const description = type.Description?.trim();

                                        return (
                                            <Table.HeaderCell className={cx('plan-cell')} textAlign="center" key={type.Id}>
                                                <div className={cx('plan-head')}>
                                                    <span className={cx('plan-name')}>{type.Name}</span>
                                                    {isSuggested || isActive ? (
                                                        <div className={cx('plan-labels')}>
                                                            {isSuggested && (
                                                                <Label horizontal color="orange">
                                                                    {translate('common.suggested')}
                                                                </Label>
                                                            )}
                                                            {isActive && (
                                                                <Label horizontal className={cx('label-active')}>
                                                                    {translate('common.Active')}
                                                                </Label>
                                                            )}
                                                        </div>
                                                    ) : null}
                                                    {description ? <p className={cx('plan-description')}>{description}</p> : null}
                                                    {type.Name && license && displayPrice ? (
                                                        <div className={cx('plan-bottom')}>
                                                            <div className={cx('price', 'plan-price')}>
                                                                <span className={cx('plan-price-value')}>{displayPrice.priceValue}</span>
                                                                <span className={cx('plan-price-unit')}>{displayPrice.unit}</span>
                                                            </div>
                                                            <div className={cx('plan-total')}>
                                                                {planPrice?.Price}&nbsp;{planPrice?.Country?.CurrencyInfo?.CurrencySign}{' '}
                                                                <span className={cx('text-transform-lowercase')}>{translate('common.total')}</span>
                                                            </div>
                                                        </div>
                                                    ) : null}
                                                    {type.Name && !planPrice ? (
                                                        <div className={cx('plan-bottom')}>
                                                            <div data-testid="missing-plan-price" className={cx('price', 'plan-price')}>
                                                                —
                                                            </div>
                                                            {/* Empty total line keeps the dash on the same line as the prices. */}
                                                            <div className={cx('plan-total')} aria-hidden="true">&nbsp;</div>
                                                        </div>
                                                    ) : null}
                                                </div>
                                            </Table.HeaderCell>
                                        );
                                    })}
                                </Table.Row>
                            </Table.Header>
                            <Table.Body>
                                {/* <Table.Row>
                        <Table.Cell
                            colSpan={2}
                            className={cx('cell-inactive')}
                        ></Table.Cell>
                        <Table.Cell colSpan={5} positive textAlign="center">
                            {translate('licenses.freeToTest')}
                        </Table.Cell>
                    </Table.Row> */}
                                {Object.keys(grouppedByInformationType).map((key, rowIndex) => {
                                    const informationTypeName = licenseInformationTypes.data.find(
                                        (l: any) => l.Id === key
                                    )?.Name;
                                    const informationByType = grouppedByInformationType[key];

                                    return informationByType.map((item, index) => (
                                        <>
                                            {rowIndex > 0 && index === 0 ? (
                                                <Table.Row textAlign="center" key={`${key}-${index}`}>
                                                    <Table.Cell
                                                        className={cx([
                                                            `license-information-type-${rowIndex}`,
                                                        ])}
                                                        textAlign="center"
                                                        colSpan={types.data.length + 1}
                                                    >
                                                        {informationTypeName}
                                                    </Table.Cell>
                                                </Table.Row>
                                            ) : null}
                                            <Table.Row key={`${key}-${index}`}>
                                                <Table.Cell
                                                    verticalAlign="middle"
                                                    key={`${key}-${index}`}
                                                >
                                                    {item.Url ? (
                                                        <a
                                                            className={cx('discrete-link')}
                                                            href={item.Url}
                                                        >
                                                            <div
                                                                className={cx([
                                                                    'flex',
                                                                    'justify-space-between',
                                                                    'align-center',
                                                                    'pl-1',
                                                                ])}
                                                            >
                                                                <span
                                                                    style={{
                                                                        width: 'calc(100% - 80px)',
                                                                        flexGrow: 1,
                                                                        textOverflow: 'ellipsis',
                                                                        overflow: 'hidden',
                                                                    }}
                                                                >
                                                                    {item.Name}
                                                                </span>
                                                                <span>
                                                                    <Info content={item.Description} />
                                                                </span>
                                                            </div>
                                                        </a>
                                                    ) : (
                                                        <div
                                                            className={cx([
                                                                'flex',
                                                                'justify-space-between',
                                                                'align-center',
                                                                'pl-1',
                                                            ])}
                                                        >
                                                            <span
                                                                style={{
                                                                    width: 'calc(100% - 80px)',
                                                                    flexGrow: 1,
                                                                    textOverflow: 'ellipsis',
                                                                    overflow: 'hidden',
                                                                }}
                                                            >
                                                                {item.Name}
                                                            </span>
                                                            <span>
                                                                <Info content={item.Description} />
                                                            </span>
                                                        </div>
                                                    )}
                                                </Table.Cell>
                                                {types.data.map((type) => {
                                                    // e.g StandardEdition => StandardEditionValue
                                                    // @ts-ignore
                                                    const valueString = item[`${type.key}Value`];
                                                    const value = valueString && valueString.includes('|')
                                                        ? valueString.split('|')
                                                        : [valueString];

                                                    return (
                                                        <Table.Cell
                                                            key={type.Id}
                                                            textAlign="center"
                                                            // active={!!companyLicenses.data[type.Id]}
                                                            positive={type.Id === suggestedLicenseId}
                                                        >
                                                            {valueString &&
                                                                value.length === 1 &&
                                                                valueString !== 'NULL' &&
                                                                item.Name !== 'own domain name'
                                                                ? value[0]
                                                                : null}
                                                            {valueString &&
                                                                value.length === 2 &&
                                                                valueString !== 'NULL' &&
                                                                item.Name !== 'own domain name' ? (
                                                                <>
                                                                    <div>{value[0]}</div>
                                                                    <small>{value[1]}</small>
                                                                </>
                                                            ) : null}
                                                            {!valueString &&
                                                                // @ts-ignore
                                                                item[type.key] ? (
                                                                <img
                                                                    src={CheckIconImg}
                                                                    alt="Info"
                                                                    className={cx('check')} />
                                                            ) : null}
                                                            {valueString === 'NULL' &&
                                                                item.FreeEdition &&
                                                                // @ts-ignore
                                                                item[type.key] ? (
                                                                <img
                                                                    src={CheckIconImg}
                                                                    alt="Info"
                                                                    className={cx('check')} />
                                                            ) : null}
                                                            {!valueString &&
                                                                // @ts-ignore
                                                                !item[type.key] ? (
                                                                <img
                                                                    src={invalidIconImg}
                                                                    alt="Invalid"
                                                                    className={cx('invalid')} />
                                                            ) : null}
                                                        </Table.Cell>
                                                    );
                                                })}
                                            </Table.Row>
                                        </>
                                    ));
                                })}
                                <Table.Row>
                                    <Table.Cell />
                                    {types.data.map((type) => {
                                        const isScheduled = !!findLicense(companyLicenses.data, type.Id, pricePlanId, billingPlanId, isScheduledRow);
                                        const isActive = !!findLicense(companyLicenses.data, type.Id, pricePlanId, billingPlanId, isLive);
                                        const planPrice = findPlanPrice(type.Prices, pricePlanId);

                                        return (
                                            <Table.Cell textAlign="center" key={type.Id}>
                                                <Button
                                                    data-testid="select-plan"
                                                    className={cx(
                                                        'btn-plan',
                                                        isActive ? 'green' : '',
                                                        isScheduled ? 'scheduled' : ''
                                                    )}
                                                    type="button"
                                                    content={isScheduled ? translate('form.scheduled') : isActive ? translate('form.selected') : translate('form.select')}
                                                    disabled={isActive || isScheduled || !planPrice}
                                                    onClick={() => this.handleSelectPlan(type.Id, pricePlanId)} />
                                            </Table.Cell>
                                        );
                                    })}
                                </Table.Row>
                                <Table.Row></Table.Row>
                            </Table.Body>
                        </Table>
                        <p className={cx('prices-footnote')}>* {translate('licenses.pricesExclVat')}</p>
                    </>
                )}
            </Segment>
        );
    };
}
function isReady(state: { isLoaded: boolean; requestIsHidden?: boolean | null }) {
    return state.isLoaded || !!state.requestIsHidden;
}

const findLicense = (
    licenses: License[],
    typeId: number,
    planId: number,
    billingPlanId: number | null | undefined,
    predicate: (l: License) => boolean
) => licenses.find((l) => l.TypeId === typeId && predicate(l) && planOf(l, billingPlanId) === planId);

const isLive = (l: License) => !l.Canceled && l.LicenseStatus !== 'Scheduled';
const isScheduledRow = (l: License) => !l.Canceled && l.LicenseStatus === 'Scheduled';

// Companies list is ordered by ValidFrom asc, so the last live application license is the current one.
export const findCurrentApplicationLicense = (licenses: License[]) =>
    [...licenses].reverse().find((l) => isLive(l) && isApplicationLicenseTypeId(l.TypeId));

